import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Stage, Layer, Rect, Group, Text, Shape } from 'react-konva';

export default function KonvaSacks({
  boxes,
  stepsPerBox,
  unitLabel,
  measureIncrement,
  measureMaxVal,
  actionable,
  onInc,
  onDec, // kept for parity
}) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 720, h: 300 });

  const PADDING = 8;
  const GAP = 16;
  const SACK_W = 130;
  const SACK_H = 170;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const cr = entries[0].contentRect;
      const width = Math.max(320, cr.width);
      const cols = Math.max(1, Math.floor((width - PADDING * 2 + GAP) / (SACK_W + GAP)));
      const rows = Math.ceil((boxes?.length || 0) / cols) || 1;
      const height = rows * (SACK_H + GAP) - GAP + PADDING * 2;
      setSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [boxes?.length]);

  const cols = useMemo(
    () => Math.max(1, Math.floor((size.w - PADDING * 2 + GAP) / (SACK_W + GAP))),
    [size.w]
  );

  const getXY = (idx) => {
    const row = Math.floor(idx / cols);
    const col = idx % cols;
    const x = PADDING + col * (SACK_W + GAP);
    const y = PADDING + row * (SACK_H + GAP);
    return { x, y };
  };

  const formatAmount = (steps) => {
    const inc = measureIncrement > 0 ? measureIncrement : 1;
    const val = steps * inc;
    return `${val.toFixed(0)}${unitLabel}`;
  };

  const handleEnter = (e) => {
    if (!actionable) return;
    e.target.getStage().container().style.cursor = 'pointer';
  };
  const handleLeave = (e) => {
    e.target.getStage().container().style.cursor = 'default';
  };

  // --- draw ONLY the sack body path (no lip/tie here) ---
  const drawSackBodyPath = (ctx, W, H) => {
    const neckY = H * 0.18;        // tie Y
    const tieH = H * 0.06;
    const bodyTop = neckY + tieH;  // start of main body
    const bodyW = W * 0.82;
    const bodyX = (W - bodyW) / 2;
    const bulge = W * 0.18;
    const bottomR = 10;

    ctx.beginPath();
    // start left shoulder just below tie
    ctx.moveTo(bodyX, bodyTop + 4);

    // left bulge
    ctx.quadraticCurveTo(
      bodyX - bulge, bodyTop + H * 0.25,
      bodyX,         bodyTop + H * 0.5
    );

    // left wall down to bottom-left corner (rounded)
    ctx.lineTo(bodyX, H - bottomR);
    ctx.quadraticCurveTo(bodyX, H, bodyX + bottomR, H);

    // bottom line to bottom-right (rounded)
    ctx.lineTo(bodyX + bodyW - bottomR, H);
    ctx.quadraticCurveTo(bodyX + bodyW, H, bodyX + bodyW, H - bottomR);

    // right wall and bulge back up
    ctx.lineTo(bodyX + bodyW, bodyTop + H * 0.5);
    ctx.quadraticCurveTo(
      bodyX + bodyW + bulge, bodyTop + H * 0.25,
      bodyX + bodyW,         bodyTop + 4
    );

    // close across top of body (under tie)
    ctx.closePath();
  };

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      <Stage width={size.w} height={size.h}>
        <Layer>
          {(boxes || []).map((steps, idx) => {
            const { x, y } = getXY(idx);
            const pct = stepsPerBox ? steps / stepsPerBox : 0;
            const fillH = Math.max(0, Math.min(SACK_H, Math.round(SACK_H * pct)));

            return (
              <Group
                key={idx}
                x={x}
                y={y}
                listening={actionable}
                onClick={actionable ? () => onInc(idx) : undefined}
                onTap={actionable ? () => onInc(idx) : undefined}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
              >
                {/* Clip interior gradient to the sack body ONLY */}
                <Group clipFunc={(ctx) => drawSackBodyPath(ctx, SACK_W, SACK_H)}>
                  <Rect
                    x={0}
                    y={SACK_H - fillH}
                    width={SACK_W}
                    height={fillH}
                    fillLinearGradientStartPoint={{ x: 0, y: SACK_H - fillH }}
                    fillLinearGradientEndPoint={{ x: 0, y: SACK_H }}
                    fillLinearGradientColorStops={[0, '#0d6efd', 1, '#084298']}
                  />
                </Group>

                {/* Outline pieces */}
                <Shape
                  sceneFunc={(ctx, shape) => {
                    // top lip (stroke only)
                    ctx.beginPath();
                    ctx.rect((SACK_W * 0.09), SACK_H * 0.10, SACK_W * 0.82, SACK_H * 0.03);
                    ctx.closePath();
                    ctx.strokeStyle = '#444';
                    ctx.lineWidth = 1.5;
                    ctx.stroke();

                    // tie/band (small filled rect near top)
                    ctx.beginPath();
                    ctx.rect((SACK_W * 0.14), SACK_H * 0.18, SACK_W * 0.72, SACK_H * 0.06);
                    ctx.closePath();
                    ctx.fillStyle = '#e9ecef';
                    ctx.fill();
                    ctx.strokeStyle = '#444';
                    ctx.stroke();

                    // sack body — STROKE ONLY (no fill, so gradient stays visible)
                    drawSackBodyPath(ctx, SACK_W, SACK_H);
                    ctx.strokeStyle = '#444';
                    ctx.lineWidth = 2;
                    ctx.stroke();
                  }}
                />

                {/* Centered amount label */}
                <Text
                  text={`${formatAmount(steps)} / ${measureMaxVal}${unitLabel}`}
                  x={0}
                  y={SACK_H / 2 - 9}
                  width={SACK_W}
                  align="center"
                  fontSize={14}
                  fill="#0d6efd"
                  listening={false}
                />
              </Group>
            );
          })}
        </Layer>
      </Stage>
    </div>
  );
}